import React, { useState, useEffect } from "react";
import {
  Container,
  Grid,
  Box,
  Card,
  CardMedia,
  Typography,
  Button,
} from "@mui/material";

const generateDeck = (images, isStandard) => {
  const deck = isStandard ? [...images, ...images] : [...images];
  return deck.sort(() => Math.random() - 0.5);
};

function decodeHtml(html) {
  var txt = document.createElement("textarea");
  txt.innerHTML = html;
  return txt.value;
}

function cleanHtml(html) {
  // remove specifically `font-size: 13pt;`
  return html.replace(/font-size:\s*13pt;?/gi, "");
}

const Pairs = ({
  defaultImage,
  images,
  timeToSolve,
  result,
  isStandard,
  isSimpleMode,
  tileBGColor
}) => {
  const [deck, setDeck] = useState(generateDeck(images, isStandard));
  const [selected, setSelected] = useState([]);
  const [matched, setMatched] = useState([]);
  const [started, setStarted] = useState(false);
  const [timer, setTimer] = useState(0);
  const [intervalId, setIntervalId] = useState(null);

  useEffect(() => {
    if (isSimpleMode && selected.length === 2) {
      const [first, second] = selected;
      if (isStandard) {
        if (deck[first] === deck[second]) {
          setMatched((prev) => [...prev, first, second]);
        }
      } else {
        if (deck[first].pair === deck[second].pair) {
          setMatched((prev) => [...prev, first, second]);
        }
      }
      setTimeout(() => setSelected([]), 800);
    }
  }, [selected, deck, isSimpleMode]);

  useEffect(() => {
    if (!isSimpleMode && selected.length === 2) {
      const [first, second] = selected;
      if (isStandard) {
        if (deck[first] === deck[second]) {
          setMatched((prev) => [...prev, first, second]);
        }
      } else {
        if (deck[first].pair === deck[second].pair) {
          setMatched((prev) => [...prev, first, second]);
        }
      }

      setTimeout(() => setSelected([]), 1000);
    }
  }, [selected, deck, isSimpleMode, isStandard]);

  useEffect(() => {
    if (started) {
      const timerInterval = setInterval(() => {
        setTimer((prev) => {
          if (prev >= Number(timeToSolve) * 60) {
            clearInterval(timerInterval);
            result(false, prev);
            return prev;
          }
          return prev + 1;
        });
      }, 1000);
      setIntervalId(timerInterval);
      return () => clearInterval(timerInterval);
    }
  }, [started]);

  useEffect(() => {
    if (matched.length === deck.length && started) {
      clearInterval(intervalId);
      result(true, timer);
    }
  }, [matched, deck, started, intervalId]);

  const handleClick = (index) => {
    if (!started) setStarted(true);
    if (
      selected.length === 2 ||
      matched.includes(index) ||
      selected.includes(index)
    )
      return;
    setSelected((prev) => [...prev, index]);
  };

  const isRevealed = (index) =>
    isSimpleMode || selected.includes(index) || matched.includes(index);

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs < 10 ? "0" : ""}${secs}`;
  };

  return (
    <Container sx={{ px: { sm: 2, xs: 0 }, maxWidth: "1600px !important" }}>
      <Box textAlign="center" my={{sm: 4, xs: 2}}>
        <Typography
          variant="h6"
          sx={{
            color: "#fff",
            fontSize: "22px",
            marginBottom: 3,
            textAlign: "left",
          }}
        >
          Time: {formatTime(timer)}
        </Typography>
        <Grid container spacing={2}>
          {deck.map((image, index) => (
            <Grid item md={3} sm={4} xs={6} key={index}>
              <Card
                onClick={() => handleClick(index)}
                sx={{
                  px: "16px",
                  py: "16px",
                  display: "flex",
                  justifyContent: "center",
                  cursor:
                    matched.includes(index) || selected.includes(index)
                      ? "default"
                      : "pointer",
                  border: matched.includes(index)
                    ? "3px solid #4caf50" // green for matched
                    : selected.includes(index)
                    ? "3px solid #2196f3" // blue for selected
                    : "1px solid #ccc",
                  backgroundColor: matched.includes(index)
                    ? "transparent"
                    : tileBGColor,
                  boxShadow: matched.includes(index)
                    ? "0 0 10px #4caf50"
                    : selected.includes(index)
                    ? "0 0 8px #2196f3"
                    : "none",
                  borderRadius: "8px",
                  aspectRatio: "2 / 1",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                {isStandard ? (
                  <CardMedia
                    component="img"
                    height="60"
                    sx={{ width: "auto", height: { sm: "45px", xs: "30px" } }}
                    image={
                      isRevealed(index)
                        ? process.env.REACT_APP_API_IMAGE_URL + image
                        : defaultImage
                        ? process.env.REACT_APP_API_IMAGE_URL + defaultImage
                        : "https://via.placeholder.com/150?text=Back"
                    }
                    alt="Card Image"
                  />
                ) : image.type === "image" ? (
                  <CardMedia
                    component="img"
                    height="60"
                    sx={{ width: "auto", height: { sm: "45px", xs: "30px" } }}
                    image={
                      isRevealed(index)
                        ? process.env.REACT_APP_API_IMAGE_URL + image.content
                        : defaultImage
                        ? process.env.REACT_APP_API_IMAGE_URL + defaultImage
                        : "https://via.placeholder.com/150?text=Back"
                    }
                    alt="Card Image"
                  />
                ) : isRevealed(index) ? (
                  <CardMedia
                    component="div"
                    height="60"
                    sx={{
                      width: "100%",
                      color: matched.includes(index) ? "#fff" : "#131B20",
                    }}
                    alt="Card Text"
                  >
                    <Typography
                      sx={{ fontSize: {md: "0.89vw", sm: "1.5vw", xs: "2.5vw"}, lineHeight: 1.2 }}
                      dangerouslySetInnerHTML={{
                        __html: decodeHtml(cleanHtml(image.content)),
                      }}
                    />
                  </CardMedia>
                ) : (
                  <CardMedia
                    component="img"
                    height="60"
                    sx={{ width: "auto", height: { sm: "45px", xs: "30px" } }}
                    image={
                      defaultImage
                        ? process.env.REACT_APP_API_IMAGE_URL + defaultImage
                        : "https://via.placeholder.com/150?text=Back"
                    }
                    alt="Card Image"
                  />
                )}
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Container>
  );
};

export default Pairs;
